import React, { useState, useEffect } from 'react';
import { auth, db, storage } from '../../firebase';
import { doc, getDoc, updateDoc } from 'firebase/firestore';
import { onAuthStateChanged, updateProfile } from 'firebase/auth';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import './UserProfile.css';

// --- IMPORTĂ POZA LOCALĂ ---
// Asigură-te că fișierul 'default_avatar.png' există în folderul 'assets'
import defaultAvatar from '../../assets/149071.png';

const UserProfile = () => {
    const [loading, setLoading] = useState(true);
    const [currentUser, setCurrentUser] = useState(null);

    // --- State-uri Imagine ---
    // Pornim direct cu poza locală
    const [photoURL, setPhotoURL] = useState(defaultAvatar);
    const [uploadingImg, setUploadingImg] = useState(false);

    // --- State-uri Date Utilizator ---
    const [heightFt, setHeightFt] = useState('');
    const [heightIn, setHeightIn] = useState('');
    const [newWeightEntry, setNewWeightEntry] = useState('');

    const [userData, setUserData] = useState({
        age: '',
        gender: 'male',
        height: '',
        weight: '',
        unitSystem: 'metric',
        weightHistory: []
    });

    // --- State-uri BMI ---
    const [bmiResult, setBmiResult] = useState(null);
    const [bmiStatus, setBmiStatus] = useState('');
    const [bmiColor, setBmiColor] = useState('#fff');

    // --- 1. Încărcare Date ---
    useEffect(() => {
        setLoading(true);
        const unsubscribe = onAuthStateChanged(auth, async (user) => {
            if (user) {
                setCurrentUser(user);

                // Setăm poza: dacă userul are una pe Firebase o punem, altfel punem defaultAvatar
                setPhotoURL(user.photoURL || defaultAvatar);

                try {
                    const docRef = doc(db, "users", user.uid);
                    const docSnap = await getDoc(docRef);

                    if (docSnap.exists()) {
                        const data = docSnap.data();

                        // Verificăm și în baza de date dacă există un URL salvat
                        if (data.photoURL) setPhotoURL(data.photoURL);

                        const unit = data.unitSystem || 'metric';

                        setUserData({
                            age: data.age || '',
                            gender: data.gender || 'male',
                            height: data.height || '',
                            weight: data.weight || '',
                            unitSystem: unit,
                            weightHistory: data.weightHistory || []
                        });

                        // Conversie pentru Imperial (afișare ft/in)
                        if (unit === 'imperial' && data.height) {
                            const totalInches = parseFloat(data.height);
                            const ft = Math.floor(totalInches / 12);
                            const inch = Math.round(totalInches % 12);
                            setHeightFt(ft);
                            setHeightIn(inch);
                        }

                        // Calcul BMI inițial
                        if (data.weight && data.height) {
                            calculateBMI(data.weight, data.height, unit);
                        }
                    }
                } catch (error) {
                    console.error("Eroare la citirea datelor:", error);
                }
            } else {
                setCurrentUser(null);
            }
            setLoading(false);
        });
        return () => unsubscribe();
    }, []);

    // --- 2. Funcție Calcul BMI ---
    const calculateBMI = (weight, height, unitSystem) => {
        if (!weight || !height) return;
        let bmi;
        if (unitSystem === 'metric') {
            const heightInMeters = height / 100;
            bmi = weight / (heightInMeters * heightInMeters);
        } else {
            bmi = (weight / (height * height)) * 703;
        }
        const finalBmi = parseFloat(bmi.toFixed(1));
        setBmiResult(finalBmi);

        if (finalBmi < 18.5) { setBmiStatus('SUBPONDERAL'); setBmiColor('#00bfff'); }
        else if (finalBmi < 24.9) { setBmiStatus('NORMAL'); setBmiColor('#00ff00'); }
        else if (finalBmi < 29.9) { setBmiStatus('SUPRAPONDERAL'); setBmiColor('#ffff00'); }
        else { setBmiStatus('OBEZITATE'); setBmiColor('#ff0000'); }
    };

    // --- 3. Handlers Input ---
    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setUserData({ ...userData, [name]: value });

        if (name === 'weight') calculateBMI(value, userData.height, userData.unitSystem);
        if (name === 'height' && userData.unitSystem === 'metric') calculateBMI(userData.weight, value, userData.unitSystem);
    };

    const handleImperialHeightChange = (e) => {
        const { name, value } = e.target;
        let newFt = name === 'feet' ? value : heightFt;
        let newIn = name === 'inches' ? value : heightIn;

        if (name === 'feet') setHeightFt(value);
        if (name === 'inches') setHeightIn(value);

        const totalInches = (parseFloat(newFt || 0) * 12) + parseFloat(newIn || 0);
        setUserData(prev => ({ ...prev, height: totalInches }));
        calculateBMI(userData.weight, totalInches, 'imperial');
    };

    const handleUnitChange = (e) => {
        const newUnit = e.target.value;
        setUserData({ ...userData, unitSystem: newUnit, height: '', weight: '' });
        setHeightFt('');
        setHeightIn('');
        setBmiResult(null);
    };

    // --- 4. Upload Imagine ---
    const handleImageChange = async (e) => {
        if (e.target.files[0] && currentUser) {
            const file = e.target.files[0];
            setUploadingImg(true);
            try {
                // 1. Upload în Firebase Storage
                const storageRef = ref(storage, `profileImages/${currentUser.uid}`);
                await uploadBytes(storageRef, file);
                const url = await getDownloadURL(storageRef);

                // 2. Update Auth Profile
                await updateProfile(currentUser, { photoURL: url });

                // 3. Update Firestore
                const userRef = doc(db, "users", currentUser.uid);
                await updateDoc(userRef, { photoURL: url });

                // 4. Update State Local
                setPhotoURL(url);
                alert("Poza a fost actualizată cu succes!");
            } catch (error) {
                console.error("Eroare la upload:", error);
                alert("Eroare la încărcarea imaginii.");
            }
            setUploadingImg(false);
        }
    };

    // --- 5. Salvare Date Personale ---
    const handleUpdateProfile = async (e) => {
        e.preventDefault();
        if (!currentUser) return;

        try {
            const userRef = doc(db, "users", currentUser.uid);
            await updateDoc(userRef, {
                age: userData.age,
                gender: userData.gender,
                height: userData.height,
                unitSystem: userData.unitSystem,
                weight: userData.weight
            });
            alert("Date salvate!");
        } catch (error) {
            console.error(error);
            alert("Eroare la salvare.");
        }
    };

    // --- 6. Adăugare Greutate în Grafic ---
    const handleAddWeightEntry = async () => {
        if (!newWeightEntry || !currentUser) return;

        const dateWithTime = new Date().toLocaleString('ro-RO', {
            day: '2-digit', month: '2-digit', year: 'numeric',
            hour: '2-digit', minute: '2-digit'
        });

        const weightValue = parseFloat(newWeightEntry);
        let updatedHistory = [...(userData.weightHistory || [])];

        // Adăugăm noua intrare
        updatedHistory.push({ date: dateWithTime, weight: weightValue });

        try {
            const userRef = doc(db, "users", currentUser.uid);
            await updateDoc(userRef, {
                weight: weightValue,
                weightHistory: updatedHistory
            });

            setUserData(prev => ({
                ...prev,
                weight: weightValue,
                weightHistory: updatedHistory
            }));

            calculateBMI(weightValue, userData.height, userData.unitSystem);
            setNewWeightEntry('');

        } catch (error) {
            console.error("Eroare la grafic:", error);
            alert("Eroare la adăugarea greutății.");
        }
    };

    if (loading) return <div className="loading">Se încarcă datele...</div>;

    return (
        <div className="user-profile-page">
            <h1 className="profile-title">Profilul Tău</h1>

            <div className="profile-content">

                {/* --- CARD 1: DATE PERSONALE --- */}
                <div className="profile-card">
                    <div className="profile-picture-section">
                        <div className="img-wrapper">
                            {/* Aici folosim logica de fallback */}
                            <img
                                src={photoURL}
                                alt="Profil"
                                className="profile-img-large"
                                onError={(e) => {
                                    e.target.onerror = null;
                                    e.target.src = defaultAvatar; // Dacă URL-ul e greșit, pune poza locală
                                }}
                            />
                            <label htmlFor="fileInput" className="upload-icon">📷</label>
                            <input type="file" id="fileInput" onChange={handleImageChange} style={{ display: 'none' }} accept="image/*" />
                        </div>
                        {uploadingImg && <p style={{color: '#0ff', fontSize: '0.8rem', marginTop:'5px'}}>Se încarcă...</p>}
                    </div>

                    <h2 className="card-title">Date Personale</h2>
                    <form onSubmit={handleUpdateProfile}>

                        <div className="form-group">
                            <label>Sistem de Măsură</label>
                            <div className="unit-toggle">
                                <label className={`unit-option ${userData.unitSystem === 'metric' ? 'active' : ''}`}>
                                    <input type="radio" value="metric" checked={userData.unitSystem === 'metric'} onChange={handleUnitChange} />
                                    Metric (cm / kg)
                                </label>
                                <label className={`unit-option ${userData.unitSystem === 'imperial' ? 'active' : ''}`}>
                                    <input type="radio" value="imperial" checked={userData.unitSystem === 'imperial'} onChange={handleUnitChange} />
                                    Imperial (ft / lbs)
                                </label>
                            </div>
                        </div>

                        <div className="form-group">
                            <label>Sex</label>
                            <select
                                name="gender"
                                value={userData.gender}
                                onChange={handleInputChange}
                                style={{
                                    width: '100%',
                                    padding: '10px',
                                    borderRadius: '5px',
                                    border: '1px solid #444',
                                    backgroundColor: '#222',
                                    color: '#fff',
                                    fontSize: '1rem'
                                }}
                            >
                                <option value="male">Masculin</option>
                                <option value="female">Feminin</option>
                            </select>
                        </div>

                        <div className="form-group">
                            <label>Vârstă</label>
                            <input type="number" name="age" value={userData.age} onChange={handleInputChange} />
                        </div>

                        <div className="form-group">
                            <label>Înălțime</label>
                            {userData.unitSystem === 'metric' ? (
                                <div className="input-wrapper">
                                    <input type="number" name="height" placeholder="cm" value={userData.height} onChange={handleInputChange} />
                                    <span className="unit-label">cm</span>
                                </div>
                            ) : (
                                <div className="imperial-height-group">
                                    <div className="input-wrapper">
                                        <input type="number" name="feet" placeholder="ft" value={heightFt} onChange={handleImperialHeightChange} />
                                        <span className="unit-label">ft</span>
                                    </div>
                                    <div className="input-wrapper">
                                        <input type="number" name="inches" placeholder="in" value={heightIn} onChange={handleImperialHeightChange} />
                                        <span className="unit-label">in</span>
                                    </div>
                                </div>
                            )}
                        </div>

                        <button type="submit" className="save-btn">Actualizează Datele</button>
                    </form>
                </div>

                {/* --- CARD 2: BMI --- */}
                <div className="profile-card bmi-card">
                    <h2 className="card-title">BMI Calculator</h2>
                    {bmiResult ? (
                        <div className="bmi-result">
                            <span className="bmi-value" style={{ color: bmiColor }}>{bmiResult}</span>
                            <span className="bmi-status" style={{ color: bmiColor }}>{bmiStatus}</span>
                            <p className="bmi-info">Calculat pentru înălțimea și greutatea ta curentă.</p>
                        </div>
                    ) : (
                        <p className="bmi-placeholder">Introdu datele complete pentru a calcula.</p>
                    )}
                </div>

                {/* --- CARD 3: GRAFIC --- */}
                <div className="profile-card chart-card full-width">
                    <h2 className="card-title">Jurnal Greutate</h2>
                    <div className="weight-add-section">
                        <input
                            type="number"
                            placeholder={`Greutate azi (${userData.unitSystem === 'metric' ? 'kg' : 'lbs'})`}
                            value={newWeightEntry}
                            onChange={(e) => setNewWeightEntry(e.target.value)}
                        />
                        <button onClick={handleAddWeightEntry} className="btn-add">+</button>
                    </div>

                    <div style={{ width: '100%', height: 300, marginTop: '20px' }}>
                        {userData.weightHistory && userData.weightHistory.length > 0 ? (
                            <ResponsiveContainer>
                                <LineChart data={userData.weightHistory}>
                                    <CartesianGrid strokeDasharray="3 3" stroke="#444" />
                                    <XAxis dataKey="date" stroke="#ccc" tick={{fontSize: 12}} />
                                    <YAxis stroke="#ccc" domain={['dataMin - 5', 'dataMax + 5']} />
                                    <Tooltip contentStyle={{backgroundColor: '#222', border: '1px solid #444'}} itemStyle={{color: '#0ff'}} />
                                    <Line type="monotone" dataKey="weight" stroke="#0ff" strokeWidth={3} dot={{r: 4}} />
                                </LineChart>
                            </ResponsiveContainer>
                        ) : (
                            <p className="no-data">Adaugă greutăți pentru a vedea progresul.</p>
                        )}
                    </div>
                </div>

            </div>
        </div>
    );
};

export default UserProfile;